import React, { useContext, useEffect, useState } from 'react';
import { CommonMessages, NewPageWrapper, useTitle } from '../../common';
import {
  IdentityProviderType,
  OidcIdentityProviderDto,
  SamlIdentityProviderDto,
} from '../../api-client';
import { CreateIdentityProviderModal } from './ui/modals/CreateIdentityProviderModal/CreateIdentityProviderModal';
import { useCreateOidcProvider } from './hooks/Oidc/useCreateOidcProvider';
import { NotificationsContext } from '../../common/notifications/Notifications';
import { useIntl } from 'react-intl';
import { useUpdateOidcProvider } from './hooks/Oidc/useUpdateOidcProvider';
import { UpdateIdentityProviderModal } from './ui/modals/UpdateIdentityProviderModal/UpdateIdentityProviderModal';
import { useQueryClient } from 'react-query';
import { useDisableOidcProvider } from './hooks/Oidc/useDisableOidcProvider';
import { useEnableOidcProvider } from './hooks/Oidc/useEnableOidcProvider';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { Flexbox, Select } from '@hiiretail/synergy-ui';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { CreateButton } from '../../common/components/Breadcrumbs/buttons/CreateButton/CreateButton';
import { useOidcProvidersQueries } from './hooks/Oidc/useOidcProvidersQueries';
import { useSetPrimaryConnectionId } from './hooks/useSetPrimaryConnectionId';
import { useCreateSamlProvider } from './hooks/Saml/useCreateSamlProvider';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { ErrorResponse } from '../../common/types/errorRespose';
import { useGetSamlProviders } from './hooks/Saml/useGetSamlProviders';
import { IdentityProvidersList } from './ui/IdentityProvidersList/IdentityProvidersList';
import {
  OIDC_PROVIDER_DISPLAYED_FIELDS,
  SAML_PROVIDER_DISPLAYED_FIELDS,
} from './ui/IdentityProvidersList/utils/DisplayedFields';
import { isOidcProvider } from './ui/IdentityProvidersList/utils/ProvidersTypeGuards';
import { useEnableSamlProvider } from './hooks/Saml/useEnableSamlProvider';
import { useDisableSamlProvider } from './hooks/Saml/useDisableSamlProvider';
import { useUpdateSamlProvider } from './hooks/Saml/useUpdateSamlProvider';
import { UpdateSamlIdentityProviderModal } from './ui/modals/UpdateSamlIdentityProviderModal/UpdateSamlIdentityProviderModal';

export const IdentityProviders: React.FC = () => {
  const intl = useIntl();
  useTitle(intl.formatMessage(CommonMessages.identityProviders));

  const [providersType, setProvidersType] = useState<IdentityProviderType>(
    IdentityProviderType.Oidc,
  );

  const [isCreateIdentityProviderModalOpen, setIsCreateIdentityProviderModalOpen] = useState(false);
  const [isUpdateIdentityProviderModalOpen, setIsUpdateIdentityProviderModalOpen] = useState(false);

  const [identityProviderToUpdate, setIdentityProviderToUpdate] = useState<
    OidcIdentityProviderDto | SamlIdentityProviderDto | null
  >(null);

  const { showNotification } = useContext(NotificationsContext);
  const queryClient = useQueryClient();

  const { updateActions } = useBreadcrumbsActions();

  useEffect(() => {
    updateActions(
      <>
        <Flexbox item>
          <RefreshButton
            onClick={
              providersType === IdentityProviderType.Oidc
                ? refetchOidcProviders
                : refetchSamlProviders
            }
            permission="iam.provider.list"
          />
        </Flexbox>
        <Flexbox item container width="125px">
          <Select
            data-testid="provider-type-select"
            options={[
              { label: 'OIDC', value: IdentityProviderType.Oidc },
              { label: 'SAML', value: IdentityProviderType.Saml },
            ]}
            fullWidth
            name="providers-type-select"
            label="Providers type"
            isClearable={false}
            isSearchable={false}
            initialValue={{ label: 'OIDC', value: IdentityProviderType.Oidc }}
            onChange={(option: Option | null) => {
              if (option) {
                setProvidersType(option.value as IdentityProviderType);
              }
            }}
            hideLabel
          />
        </Flexbox>
        <Flexbox item>
          <CreateButton
            title="New provider"
            onClick={toggleCreateIdentityProviderModalOpen}
            permission="iam.provider.create"
          />
        </Flexbox>
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providersType]);

  const { oidcProviders, primaryConnectionId, isLoading, fetchError, refetchOidcProviders } =
    useOidcProvidersQueries();

  const {
    data: samlProviders,
    isFetching: isLoadingSamlProviders,
    error: samlProvidersError,
    refetch: refetchSamlProviders,
  } = useGetSamlProviders();

  const {
    mutate: createOidcProvider,
    isLoading: isCreatingOidcProvider,
    error: createOidcProviderError,
  } = useCreateOidcProvider({
    onSuccess: ({ data: { name } }) => {
      showNotification(`Provider ${name} created successfully`);
      toggleCreateIdentityProviderModalOpen();
      queryClient.invalidateQueries('oidc-providers').then();
    },
  });

  const {
    mutate: createSamlProvider,
    isLoading: isCreatingSamlProvider,
    error: createSamlProviderError,
  } = useCreateSamlProvider({
    onSuccess: ({ data: { name } }) => {
      showNotification(`Provider ${name} created successfully`);
      toggleCreateIdentityProviderModalOpen();
      queryClient.invalidateQueries('saml-providers').then();
    },
  });

  const {
    mutate: updateIdentityProvider,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateOidcProvider({
    onSuccess: () => {
      showNotification(`Identity provider information updated successfully`);
      toggleUpdateIdentityProviderModalOpen();
      queryClient.invalidateQueries('oidc-providers').then();
    },
  });

  const {
    mutate: updateSamlIdentityProvider,
    isLoading: isUpdatingSaml,
    error: updateErrorSaml,
  } = useUpdateSamlProvider({
    onSuccess: () => {
      showNotification(`Identity provider information updated successfully`);
      toggleUpdateIdentityProviderModalOpen();
      queryClient.invalidateQueries('saml-providers').then();
    },
  });

  const { mutate: enableIdentityProvider, isLoading: isEnabling } = useEnableOidcProvider({
    onSuccess: () => {
      showNotification(`Identity provider enabled successfully`);
      queryClient.invalidateQueries('oidc-providers').then();
    },
    onError: (err) => {
      showNotification(err.message);
    },
  });

  const { mutate: disableIdentityProvider, isLoading: isDisabling } = useDisableOidcProvider({
    onSuccess: () => {
      showNotification(`Identity provider disabled successfully`);
      queryClient.invalidateQueries('oidc-providers').then();
    },
    onError: (err) => {
      showNotification(err.message);
    },
  });

  const { mutate: enableSamlIdentityProvider, isLoading: isEnablingSaml } = useEnableSamlProvider({
    onError: (err) => {
      showNotification(err.message);
    },
    onSuccess: () => {
      showNotification(`Identity provider enabled successfully`);
      queryClient.invalidateQueries('saml-providers').then();
    },
  });

  const { mutate: disableSamlIdentityProvider, isLoading: isDisablingSaml } =
    useDisableSamlProvider({
      onError: (err) => {
        showNotification(err.message);
      },
      onSuccess: () => {
        showNotification(`Identity provider disabled successfully`);
        queryClient.invalidateQueries('saml-providers').then();
      },
    });

  const { mutate: setPrimaryConnectionId, isLoading: isSetting } = useSetPrimaryConnectionId({
    onSuccess: () => {
      showNotification(`Identity provider successfully set as primary`);
      queryClient.invalidateQueries('loginConfig').then();
    },
    onError: (err) => {
      showNotification(err.message);
    },
  });

  const { mutate: unsetPrimaryConnectionId, isLoading: isUnsetting } = useSetPrimaryConnectionId({
    onSuccess: () => {
      showNotification(`Primary identity provider successfully unset`);
      queryClient.invalidateQueries('loginConfig').then();
    },
    onError: (err) => {
      showNotification(err.message);
    },
  });

  const toggleCreateIdentityProviderModalOpen = () => {
    setIsCreateIdentityProviderModalOpen((prevState) => !prevState);
  };

  const toggleUpdateIdentityProviderModalOpen = () => {
    setIsUpdateIdentityProviderModalOpen((prevState) => !prevState);
  };

  const handleUpdateButtonClick = (
    identityProviderDto: OidcIdentityProviderDto | SamlIdentityProviderDto,
  ) => {
    setIdentityProviderToUpdate(identityProviderDto);
    toggleUpdateIdentityProviderModalOpen();
  };

  return (
    <NewPageWrapper>
      {isCreateIdentityProviderModalOpen && (
        <CreateIdentityProviderModal
          onClose={toggleCreateIdentityProviderModalOpen}
          isOpen={isCreateIdentityProviderModalOpen}
          isCreating={isCreatingOidcProvider || isCreatingSamlProvider}
          error={createOidcProviderError ?? createSamlProviderError}
          createOidcProvider={createOidcProvider}
          createSamlProvider={createSamlProvider}
        />
      )}
      {identityProviderToUpdate &&
        (isOidcProvider(identityProviderToUpdate) ? (
          <UpdateIdentityProviderModal
            onClose={toggleUpdateIdentityProviderModalOpen}
            isOpen={isUpdateIdentityProviderModalOpen}
            isUpdating={isUpdating}
            error={updateError}
            identityProvider={identityProviderToUpdate}
            updateIdentityProvider={updateIdentityProvider}
          />
        ) : (
          <UpdateSamlIdentityProviderModal
            onClose={toggleUpdateIdentityProviderModalOpen}
            isOpen={isUpdateIdentityProviderModalOpen}
            isUpdating={isUpdatingSaml}
            error={updateErrorSaml}
            identityProvider={identityProviderToUpdate}
            updateIdentityProvider={updateSamlIdentityProvider}
          />
        ))}
      {providersType === IdentityProviderType.Oidc && (
        <IdentityProvidersList
          isLoading={isLoading}
          identityProviders={oidcProviders}
          primaryConnectionId={primaryConnectionId}
          onSetPrimaryButtonClick={setPrimaryConnectionId}
          onUnSetPrimaryButtonClick={unsetPrimaryConnectionId}
          refetchIdentityProviders={refetchOidcProviders}
          onUpdateButtonClick={handleUpdateButtonClick}
          onDisableButtonClick={disableIdentityProvider}
          onEnableButtonClick={enableIdentityProvider}
          isDisabling={isDisabling}
          isEnabling={isEnabling}
          isSetting={isSetting || isUnsetting}
          fetchError={fetchError as ErrorResponse}
          displayedFields={OIDC_PROVIDER_DISPLAYED_FIELDS}
        />
      )}
      {providersType === IdentityProviderType.Saml && (
        <IdentityProvidersList
          isLoading={isLoadingSamlProviders}
          identityProviders={samlProviders}
          primaryConnectionId={primaryConnectionId}
          refetchIdentityProviders={refetchSamlProviders}
          onSetPrimaryButtonClick={setPrimaryConnectionId}
          onUnSetPrimaryButtonClick={unsetPrimaryConnectionId}
          onUpdateButtonClick={handleUpdateButtonClick}
          onEnableButtonClick={enableSamlIdentityProvider}
          onDisableButtonClick={disableSamlIdentityProvider}
          isDisabling={isDisablingSaml}
          isEnabling={isEnablingSaml}
          isSetting={isSetting || isUnsetting}
          fetchError={samlProvidersError as ErrorResponse}
          displayedFields={SAML_PROVIDER_DISPLAYED_FIELDS}
        />
      )}
    </NewPageWrapper>
  );
};
